import { type CountryCode } from '@constants'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { AppliedStatus, type SelectedCouponGiveaway, type SelectedCouponGiveawayProduct } from '@hooks-api/types/dataModel/coupon'
import { useCallback, useMemo } from 'react'
import * as couponService from '../core/couponService'
import { parseGiveaways } from '../parser/parseGiveaways'
import { type MergedCoupon, type SelectedCouponGiveawayProductMap } from '../types'
import { useFpCampaignCartSelector, useFpCampaignCartStore } from './useFpCampaignCartStore'
import { useFpCampaignCoupons } from './useFpCampaignCoupons'

export const useFpCampaignCouponService = (campaignId: number | null, countryCode: CountryCode, campaignCode: string | undefined) => {
  const [, setCartValues] = useFpCampaignCartStore(campaignId)
  const coupon = useFpCampaignCartSelector(campaignId, state => state.coupon)
  const selectedGiveaways = coupon.selectedGiveaways
  const selectedRegularGiveaways = coupon.selectedRegularGiveaways

  const { availableMergedCoupons, calculateResult, isLoadingAvailableCoupons } = useFpCampaignCoupons(campaignId, countryCode, campaignCode)

  // NOTE: 已達標但已贈完後端欄位會是 unable，也要顯示出來
  const appliedGiveaways = useMemo(() => parseGiveaways(availableMergedCoupons)?.filter(giveaway => giveaway.applied_status === AppliedStatus.APPLIED || giveaway.applied_status === AppliedStatus.UNABLE), [availableMergedCoupons])

  const updateSelectedRegularGiveaways = useCallbackEvent((
    couponCriterionId: SelectedCouponGiveaway['coupon_criterion_id'],
    product: Omit<SelectedCouponGiveawayProduct, 'quantity'>,
  ) => {
    setCartValues((state) => {
      return ({
        ...state,
        coupon: couponService.updateSelectedRegularGiveaways(state.coupon, couponCriterionId, product),
      })
    })
  })

  const updateSelectedGiveaways = useCallbackEvent((
    couponCriterionId: SelectedCouponGiveaway['coupon_criterion_id'],
    couponGiveawayProductMap: SelectedCouponGiveawayProductMap,
  ) => {
    setCartValues(state => ({
      ...state,
      coupon: couponService.updateSelectedGiveaways(state.coupon, couponCriterionId, couponGiveawayProductMap),
    }))
  })

  const addManualInputCouponIds = useCallbackEvent((couponId: number[]) => {
    setCartValues(state => ({
      ...state,
      coupon: couponService.addManualInputCouponIds(state.coupon, couponId),
    }))
  })

  const removeManualInputCouponIds = useCallbackEvent((couponId: number[]) => {
    setCartValues(state => ({
      ...state,
      coupon: couponService.removeManualInputCouponIds(state.coupon, couponId),
    }))
  })

  const removeSelectedRegularGiveaways = useCallbackEvent((
    couponCriterionIds: SelectedCouponGiveaway['coupon_criterion_id'] | SelectedCouponGiveaway['coupon_criterion_id'][],
  ) => {
    setCartValues(state => ({
      ...state,
      coupon: couponService.removeSelectedRegularGiveaways(state.coupon, couponCriterionIds),
    }))
  })

  const clearInvalidSelectedGiveaways = useCallback(() => {
    setCartValues(state => ({
      ...state,
      coupon: couponService.clearInvalidSelectedGiveaways(state.coupon, calculateResult?.applied_coupons),
    }))
  }, [calculateResult?.applied_coupons, setCartValues])

  const clearInvalidManualInputCouponIds = useCallback(() => {
    setCartValues(state => ({
      ...state,
      coupon: couponService.clearInvalidManualInputCouponIds(state.coupon, calculateResult?.cart_validation_messages),
    }))
  }, [calculateResult?.cart_validation_messages, setCartValues])

  const selectedCouponGiveawayMap = useMemo(() => {
    return couponService.getSelectedCouponGiveawayMap([...selectedRegularGiveaways, ...selectedGiveaways])
  },
  [selectedRegularGiveaways, selectedGiveaways])

  const setDefaultSelectedGiveaways = useCallback(() => {
    if (appliedGiveaways) {
      setCartValues(state => ({
        ...state,
        coupon: couponService.setDefaultSelectedGiveaways(state.coupon, appliedGiveaways),
      }))
    }
  }, [appliedGiveaways, setCartValues])

  const resetSelectedGiveaways = useCallbackEvent(() => {
    setCartValues(state => ({
      ...state,
      coupon: couponService.resetSelectedGiveaways(state.coupon),
    }))
  })

  const resetSelectedRegularGiveaways = useCallbackEvent(() => {
    setCartValues(state => ({
      ...state,
      coupon: couponService.resetSelectedRegularGiveaways(state.coupon),
    }))
  })

  const resetManualInputCouponIds = useCallbackEvent(() => {
    setCartValues(state => ({
      ...state,
      coupon: couponService.resetManualInputCouponIds(state.coupon),
    }))
  })

  const resetCouponsAndGiveaways = useCallbackEvent(() => {
    resetManualInputCouponIds()
    resetSelectedGiveaways()
    resetSelectedRegularGiveaways()
  })

  const regularGiveaways = useMemo(() => availableMergedCoupons?.filter(coupon => coupon.is_regular_giveaway), [availableMergedCoupons])

  const getRegularGiveawayProgress = useCallback((regularGiveaways: MergedCoupon[]) => couponService.getRegularGiveawayProgress(regularGiveaways, calculateResult?.total_after_discount), [calculateResult?.total_after_discount])

  return {
    ...coupon,
    regularGiveaways,
    getRegularGiveawayProgress,
    appliedGiveaways,
    updateSelectedRegularGiveaways,
    removeSelectedRegularGiveaways,
    setDefaultSelectedGiveaways,
    selectedCouponGiveawayMap,
    isLoading: isLoadingAvailableCoupons,
    addManualInputCouponIds,
    removeManualInputCouponIds,
    updateSelectedGiveaways,
    clearInvalidSelectedGiveaways,
    clearInvalidManualInputCouponIds,
    resetCouponsAndGiveaways,
  }
}
